import {
  checkType,
  Plugins,
  withMeta,
  Expressions,
  TypeImplementation,
  withSystem,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  updateExpressionAction,
  type ExpressionEditorDispatch,
} from '@packages/systems/ExpressionEditor-utils';
import {
  TNumber,
  TRecord,
  TText,
  ELiteral,
  ENumber,
  ERecord,
  EText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {usePlugins} from '@packages/utilities/platform';
import {matchJson, get, Json} from '@packages/utilities/json';
import {typeName, qualify} from '@packages/systems/wfdl-Qualified';
import React from 'react';
import SharedConfig from '@packages/systems/core/constants/SharedConfig';

import {
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {DesignerFlux, useFluxContext} from '@packages/systems/designer/flux';

const {getIn} = ContextualExpressions;

const type = TRecord({
  _id: withSystem({defaultValue: SharedConfig.paths.EXAMPLE_IMG_ID}, TText),
  fileName: withSystem(
    {defaultValue: SharedConfig.paths.EXAMPLE_IMG_NAME},
    TText
  ),
  fileSize: withSystem({defaultValue: 2063}, TNumber),
  height: withSystem({defaultValue: 150}, TNumber),
  origFileName: withSystem(
    {defaultValue: SharedConfig.paths.EXAMPLE_IMG_NAME},
    TText
  ),
  type: withSystem({defaultValue: 'image'}, TText),
  url: withSystem({defaultValue: SharedConfig.paths.EXAMPLE_IMG_URL}, TText),
  width: withSystem({defaultValue: 150}, TNumber),
});

const constantNull = (
  _:
    | null
    | number
    | boolean
    | {
        [key: string]: Json;
      }
    | Array<Json>
) => null;
const matchJsonString = (
  json:
    | boolean
    | number
    | string
    | Array<Json>
    | {
        [key: string]: Json;
      }
) =>
  matchJson({
    Null: constantNull,
    Boolean: constantNull,
    Number: constantNull,
    String: (s) => s,
    Array: constantNull,
    Object: constantNull,
  })(json);

const assetImageType = qualify(Plugins.name('Asset'), typeName('Image'));

export const LightboxImageType = TypeImplementation.Alias({
  type,
  render: function LightboxImageRender({expr, dispatch}) {
    const plugins = usePlugins();
    const flux = useFluxContext<DesignerFlux>();
    const imageIdExpr = getIn(expr, [{in: 'Record', at: '_id'}]);

    if (!imageIdExpr) return null;

    const imageId = ContextualExpressions.getText(stripAnn(imageIdExpr));

    const [, imgExpr] = checkType(
      ELiteral({
        name: assetImageType,
        value: {id: imageId},
      }),
      withMeta(
        {type: 'image'},
        withSystem({label: 'Image'}, TTypeConstructor(assetImageType))
      )
    )(Plugins.buildTypeCheckerState(plugins));

    const onImageChange: ExpressionEditorDispatch = (action) => {
      if (action.type === 'EXPRESSION_ACTION') {
        const {newExpression} = action.payload;

        const literal = Expressions.getLiteral(newExpression);

        if (!literal) return null;

        const {value} = literal;

        if (!value) return null;

        const jsonValue = get(value, ['id']);

        if (!jsonValue) return null;

        const assetId = matchJsonString(jsonValue);

        if (!assetId) return null;

        const asset = flux.getStoreState('AssetStore').getById(assetId);

        if (!asset) return null;

        const nextExpr = ERecord({
          _id: EText(asset.get('_id')),
          fileName: EText(asset.get('origFileName')),
          fileSize: ENumber(asset.get('fileSize')),
          height: ENumber(asset.get('height') as number),
          origFileName: EText(asset.get('origFileName')),
          type: EText('image'),
          url: EText(asset.get('cdnUrl')),
          width: ENumber(asset.get('width') as number),
        });

        dispatch(updateExpressionAction(nextExpr));
      }
    };
    return <ExpressionEditor expression={imgExpr} dispatch={onImageChange} />;
  },
});
